import { HDate, HebrewCalendar, months } from '@hebcal/core';

/**
 * Convierte una fecha gregoriana a fecha hebrea
 * @param {Date} date - Fecha gregoriana
 * @returns {Object} Fecha hebrea con año, mes, día, nombre del mes
 */
export function toHebrewDate(date) {
  const hdate = new HDate(date);
  
  return {
    year: hdate.getFullYear(),
    month: hdate.getMonth(),
    day: hdate.getDate(),
    monthName: hdate.getMonthName(),
    hebrewYear: hdate.getFullYear(),
  };
}

/**
 * Obtiene la fecha dual (gregoriana y hebrea) considerando que el día hebreo
 * comienza a las 6 PM del día anterior
 * @param {Date} date - Fecha a convertir
 * @param {string} hebrewDayStart - Hora de inicio del día hebreo (formato "HH:mm")
 * @returns {Object} Objeto con fecha gregoriana, fecha hebrea e indicador de día hebreo
 */
export function getDualDate(date, hebrewDayStart = '18:00') {
  const [startHour, startMinute] = hebrewDayStart.split(':').map(Number);
  const currentHour = date.getHours();
  const currentMinute = date.getMinutes();
  
  // Calcular si estamos en el día hebreo actual o el siguiente
  const isAfterHebrewStart = 
    currentHour > startHour || 
    (currentHour === startHour && currentMinute >= startMinute);
  
  // Si es después de las 6 PM, el día hebreo ya cambió al siguiente día gregoriano
  const hebrewDateBase = isAfterHebrewStart 
    ? new Date(date.getTime() + 24 * 60 * 60 * 1000) // Día siguiente
    : date;
  
  return {
    gregorian: date,
    hebrew: toHebrewDate(hebrewDateBase),
    isHebrewDay: isAfterHebrewStart,
  };
}

/**
 * Formatea una fecha hebrea como string
 * @param {Object} hebrewDate - Fecha hebrea
 * @param {string} locale - Idioma (por defecto 'es')
 * @returns {string} Fecha formateada
 */
export function formatHebrewDate(hebrewDate, locale = 'es') {
  // Formato: "15 de Tishrei, 5784"
  return `${hebrewDate.day} de ${hebrewDate.monthName}, ${hebrewDate.hebrewYear}`;
}

/**
 * Obtiene el nombre del mes hebreo
 * @param {number} month - Número del mes
 * @param {boolean} isLeapYear - Si es año bisiesto
 * @returns {string} Nombre del mes
 */
export function getHebrewMonthName(month, isLeapYear = false) {
  const monthNames = months.MONTH_NAMES(isLeapYear);
  return monthNames[month] || '';
}

/**
 * Obtiene las festividades hebreas para una fecha específica
 * @param {Date} date - Fecha a consultar
 * @returns {string[]} Array de nombres de festividades
 */
export function getHebrewHolidays(date) {
  try {
    const hdate = new HDate(date);
    const holidays = HebrewCalendar.getHolidaysOnDate(hdate);
    
    return holidays.map(holiday => {
      try {
        // Intentar diferentes métodos de renderizado
        if (typeof holiday.render === 'function') {
          return holiday.render('es') || holiday.render() || holiday.getDesc() || holiday.toString();
        }
        if (holiday.getDesc && typeof holiday.getDesc === 'function') {
          return holiday.getDesc();
        }
        if (holiday.desc) {
          return holiday.desc;
        }
        return holiday.toString();
      } catch {
        return 'Festividad';
      }
    }).filter(name => name && name.trim() !== '');
  } catch (error) {
    console.warn('Error obteniendo festividades hebreas:', error);
    return [];
  }
}

/**
 * Verifica si una fecha es Shabat
 * @param {Date} date - Fecha a verificar
 * @returns {boolean} True si es sábado
 */
export function isShabat(date) {
  return date.getDay() === 6; // Sábado
}

/**
 * Obtiene el inicio y fin del Shabat para una semana dada
 * @param {Date} date - Fecha de referencia
 * @param {string} hebrewDayStart - Hora de inicio (formato "HH:mm")
 * @returns {Object} Objeto con fechas de inicio y fin del Shabat
 */
export function getShabatTimes(date, hebrewDayStart = '18:00') {
  const [startHour, startMinute] = hebrewDayStart.split(':').map(Number);
  
  // Encontrar el viernes de la semana
  const dayOfWeek = date.getDay();
  const daysUntilFriday = (5 - dayOfWeek + 7) % 7;
  const friday = new Date(date);
  friday.setDate(friday.getDate() + daysUntilFriday);
  
  // Inicio del Shabat: Viernes a las 6 PM
  const shabatStart = new Date(friday);
  shabatStart.setHours(startHour, startMinute, 0, 0);
  
  // Fin del Shabat: Sábado a las 6 PM
  const shabatEnd = new Date(shabatStart);
  shabatEnd.setDate(shabatEnd.getDate() + 1);
  
  return {
    start: shabatStart,
    end: shabatEnd,
  };
}

/**
 * Convierte una fecha hebrea a gregoriana
 * @param {number} hebrewYear - Año hebreo
 * @param {number} hebrewMonth - Mes hebreo
 * @param {number} hebrewDay - Día hebreo
 * @returns {Date} Fecha gregoriana
 */
export function hebrewToGregorian(hebrewYear, hebrewMonth, hebrewDay) {
  const hdate = new HDate(hebrewDay, hebrewMonth, hebrewYear);
  return hdate.greg();
}

/**
 * Obtiene el año hebreo actual
 * @returns {number} Año hebreo actual
 */
export function getCurrentHebrewYear() {
  const hdate = new HDate();
  return hdate.getFullYear();
}

/**
 * Obtiene todos los meses del año hebreo
 * @param {number} year - Año hebreo
 * @returns {string[]} Array con nombres de meses
 */
export function getHebrewMonths(year) {
  const hdate = new HDate(1, 1, year);
  const isLeapYear = hdate.isLeapYear();
  return months.MONTH_NAMES(isLeapYear);
}



